'use client';

import {RecaptchaScript} from '@gfiber/third-parties/google';
import {US_STATES} from '@gfiber/typesmith/us_states';
import {zodResolver} from '@hookform/resolvers/zod';
import {Typography} from '@pixel-party/Typography/v2';
import type React from 'react';
import {useId} from 'react';
import {Controller, type SubmitHandler, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {TermsAndConditions} from '@/app/forms/_components/TermsAndConditions/TermsAndConditions';
import {useSubmit} from '@/app/forms/_hooks/useSubmit';
import {REGISTRY} from '@/app/forms/_registry/client';
import * as constants from '@/app/forms/constants';
import {I_AM_MAP, YES} from '@/app/forms/i18n';
import {ApiError, type InferFormValues} from '@/app/forms/types';
import {
  Checkbox,
  FormError,
  Radio,
  SelectField,
  SelectItem,
  TextField,
} from '@/components';
import {isRequired, maxLength} from '@/lib/utils/common';
import {
  getRootError,
  getSchemaShape,
  handleError,
  onSubmitError,
  tSafe,
} from '../../utils';
import {FormDescription} from '../Description/Description';
import {SubmitButton} from '../SubmitButton/SubmitButton';
import {SuccessMessage} from '../SuccessMessage/SuccessMessage';
import styles from './FiberInterestCityOfficialForm.module.scss';

function FiberInterestCityOfficialForm(): React.JSX.Element {
  const {t} = useTranslation();

  const config = REGISTRY.FiberInterestCityOfficialForm;
  type FiberInterestCityOfficialFormData = InferFormValues<typeof config>;

  const {
    control,
    formState: {errors, isSubmitted, isValid},
    handleSubmit,
    setError,
  } = useForm<FiberInterestCityOfficialFormData>({
    defaultValues: {
      iam: '',
      interest: '',
      entityName: '',
      role: '',
      city: '',
      state: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      termsAndConditions: '',
    },
    mode: 'onChange',
    resolver: zodResolver(config.schema),
  });
  const fields = getSchemaShape(config.schema);
  const {mutate, isPending, isSuccess} =
    useSubmit<FiberInterestCityOfficialFormData>(config.id);
  const id = useId();

  const onSubmit: SubmitHandler<
    FiberInterestCityOfficialFormData
  > = async data => {
    const recaptchaToken = await window.grecaptcha?.enterprise.execute(
      constants.RECAPTCHA_SITE_KEY,
      {action: config.recaptchaAction || 'submit_form'},
    );

    if (!recaptchaToken) {
      handleError(
        new ApiError(t('errors.recaptcha'), {
          code: 'RECAPTCHA_FAILED',
          message: 'reCAPTCHA token could not be obtained',
        }),
        setError,
        fields,
      );
      return;
    }

    mutate(
      {data, recaptchaToken},
      {
        onError: error => {
          handleError(error, setError, fields);
        },
      },
    );
  };

  return (
    <>
      <h1 className="title">{t(config.title)}</h1>
      {isSuccess ? (
        <SuccessMessage />
      ) : (
        <form
          className="form"
          onSubmit={handleSubmit(onSubmit, onSubmitError)}
          noValidate
        >
          <fieldset disabled={isPending}>
            <FormDescription />
            <fieldset className="field">
              <legend className="legend">
                <Typography use="body2">
                  {t('forms.fiberInterestCityOfficial.iamLabel')}{' '}
                  {isRequired(fields.iam) && '*'}
                </Typography>
              </legend>
              <Controller
                name="iam"
                control={control}
                render={({field: {onChange, value, ref}}) => (
                  <div
                    ref={ref}
                    className="radioGroup"
                  >
                    {I_AM_MAP.map(item => (
                      <Radio
                        key={item.id}
                        id={`iam-${item.id}`}
                        name="iam"
                        value={item.value}
                        label={t(item.i18nKey, {defaultValue: item.id})}
                        checked={value === item.value}
                        onChange={onChange}
                      />
                    ))}
                  </div>
                )}
              />
              <FormError message={tSafe(errors.iam?.message, t)} />
            </fieldset>
            <div className="field">
              <Controller
                control={control}
                name="interest"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterestCityOfficial.interest')}
                    id={`interest-${id}`}
                    required={isRequired(fields.interest)}
                    errorText={tSafe(errors.interest?.message, t)}
                    maxLength={maxLength(fields.interest)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="entityName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterestCityOfficial.entityName')}
                    id={`entityName-${id}`}
                    required={isRequired(fields.entityName)}
                    errorText={tSafe(errors.entityName?.message, t)}
                    maxLength={maxLength(fields.entityName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="role"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterestCityOfficial.role')}
                    id={`role-${id}`}
                    required={isRequired(fields.role)}
                    errorText={tSafe(errors.role?.message, t)}
                    maxLength={maxLength(fields.role)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="city"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterestCityOfficial.city')}
                    id={`city-${id}`}
                    required={isRequired(fields.city)}
                    errorText={tSafe(errors.city?.message, t)}
                    maxLength={maxLength(fields.city)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                name="state"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <SelectField
                    {...field}
                    placeholder={t('forms.shared.selectOne')}
                    invalid={!!error}
                    label={t('forms.fiberInterestCityOfficial.state')}
                    required={isRequired(fields.state)}
                    errorText={tSafe(errors.state?.message, t)}
                  >
                    {US_STATES.map(state => (
                      <SelectItem
                        key={state.code}
                        value={state.code}
                      >
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectField>
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="firstName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterestCityOfficial.firstName')}
                    id={`firstName-${id}`}
                    required={isRequired(fields.firstName)}
                    errorText={tSafe(errors.firstName?.message, t)}
                    maxLength={maxLength(fields.firstName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="lastName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterestCityOfficial.lastName')}
                    id={`lastName-${id}`}
                    required={isRequired(fields.lastName)}
                    errorText={tSafe(errors.lastName?.message, t)}
                    maxLength={maxLength(fields.lastName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="email"
                render={({field}) => (
                  <TextField
                    {...field}
                    type="email"
                    label={t('forms.fiberInterestCityOfficial.email')}
                    id={`email-${id}`}
                    required={isRequired(fields.email)}
                    errorText={tSafe(errors.email?.message, t)}
                    maxLength={maxLength(fields.email)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="phoneNumber"
                render={({field}) => (
                  <TextField
                    {...field}
                    type="tel"
                    label={t('forms.fiberInterestCityOfficial.phoneNumber')}
                    id={`phoneNumber-${id}`}
                    required={isRequired(fields.phoneNumber)}
                    errorText={tSafe(errors.phoneNumber?.message, t)}
                  />
                )}
              />
            </div>
            <div className="field">
              <div className={styles.terms}>
                <Controller
                  control={control}
                  name="termsAndConditions"
                  render={({field: {onChange, value}}) => (
                    <Checkbox
                      checked={value === YES}
                      onCheckedChange={checked => onChange(checked ? YES : '')}
                      id={`termsAndConditions-${id}`}
                    />
                  )}
                />
                <TermsAndConditions htmlFor={`termsAndConditions-${id}`} />
              </div>
              <FormError
                message={tSafe(errors.termsAndConditions?.message, t)}
              />
            </div>
            <SubmitButton
              isDisabled={isPending}
              isPending={isPending}
              rootError={getRootError(errors, isSubmitted, isValid, t)}
            />
          </fieldset>
          {constants.RECAPTCHA_SITE_KEY && (
            <RecaptchaScript siteKey={constants.RECAPTCHA_SITE_KEY} />
          )}
        </form>
      )}
    </>
  );
}

export {FiberInterestCityOfficialForm};
